import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Select,
  Switch,
  Tag,
  Text,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import { MdArrowBack } from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import { useHelpArticle, HelpArticleDto } from '../hooks/useHelpArticles';
import { useHelpCategories } from '../hooks/useHelpCategories';
import { useCreateHelpArticle, CreateHelpArticleInput } from '../hooks/useCreateHelpArticle';
import { useUpdateHelpArticle } from '../hooks/useUpdateHelpArticle';
import { useUploadHelpImage } from '../hooks/useUploadHelpImage';
import InlineErrorState from '../../../components/states/InlineErrorState';
import TipTapEditor from './TipTapEditor';
import { translate } from '../../../i18n';

const slugify = (text: string) =>
  text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

export default function HelpArticleEditor() {
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;
  const navigate = useNavigate();
  const toast = useToast();

  /**
   * The one read on this screen whose failure could DESTROY an article rather
   * than merely misreport one.
   *
   * `useHelpArticle` fills the form from `existingArticle` in an effect. When
   * the read failed, `articleLoading` is false and `existingArticle` is
   * undefined, so the guard below fell through and rendered the editor with
   * every field at its initial value — empty title, empty slug, empty content —
   * with "Save" and "Publish" live. One click then PATCHed the real article
   * with blanks: the read that failed was the only copy of the content the form
   * was about to overwrite.
   *
   * `GET help/admin/articles/:id` carries `@UseGuards(AdminAuthGuard)`
   * (`api/src/help-center/help-center.controller.ts`, read this run), so a
   * refusal is reachable without any outage at all.
   *
   * Both guards require the DATA to be absent as well as the flag raised: a
   * failed background refetch leaves a correctly populated form on screen, and
   * withholding the editor then would itself lose the operator's edits.
   */
  const { data: existingArticle, isLoading: articleLoading, isError: articleError } =
    useHelpArticle(id || '');
  const articleUnreadable = isEditing && articleError && !existingArticle;

  const { data: categories, isError: categoriesError } = useHelpCategories();
  const categoriesUnavailable = categoriesError && !categories;
  const uploadImage = useUploadHelpImage();

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [contentKey, setContentKey] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [content, setContent] = useState('');
  const [language, setLanguage] = useState<'fr' | 'en'>('fr');
  const [status, setStatus] = useState<'draft' | 'published'>('draft');
  const [categoryId, setCategoryId] = useState('');
  const [coverImage, setCoverImage] = useState('');
  const [sortOrder, setSortOrder] = useState(0);

  // Populate form when editing
  useEffect(() => {
    if (existingArticle && isEditing) {
      setTitle(existingArticle.title);
      setSlug(existingArticle.slug);
      setSlugManuallyEdited(true);
      setContentKey(existingArticle.contentKey);
      setExcerpt(existingArticle.excerpt || '');
      setContent(existingArticle.content);
      setLanguage(existingArticle.language as 'fr' | 'en');
      setStatus(existingArticle.status as 'draft' | 'published');
      setCategoryId(existingArticle.categoryId);
      setCoverImage(existingArticle.coverImage || '');
      setSortOrder(existingArticle.sortOrder);
    }
  }, [existingArticle, isEditing]);

  // Auto-set first category
  useEffect(() => {
    if (categories?.length && !categoryId) {
      setCategoryId(categories[0].id);
    }
  }, [categories, categoryId]);

  const createMutation = useCreateHelpArticle((data: HelpArticleDto) => {
    toast({ title: translate('help_center.article_created'), status: 'success', duration: 3000 });
    navigate(`/admin/help-center/${data.id}/edit`);
  });

  const updateMutation = useUpdateHelpArticle(() => {
    toast({ title: translate('help_center.article_updated'), status: 'success', duration: 3000 });
  });

  const handleImageUpload = useCallback(async (file: File): Promise<string> => {
    const result = await uploadImage.mutateAsync(file);
    return result.url;
  }, [uploadImage]);

  const handleSave = (saveStatus: 'draft' | 'published') => {
    const payload: CreateHelpArticleInput = {
      title,
      slug,
      contentKey,
      excerpt: excerpt || undefined,
      content,
      language,
      status: saveStatus,
      categoryId,
      coverImage: coverImage || undefined,
      sortOrder,
    };

    if (isEditing && id) {
      updateMutation.mutate({ id, ...payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const isSaving = createMutation.isLoading || updateMutation.isLoading;
  const isPublished = isEditing && status === 'published';

  if (isEditing && articleLoading) {
    return <Box p={6}><Text color="gray.500">Loading article...</Text></Box>;
  }

  /**
   * The editor is WITHHELD rather than decorated with a notice: an empty form
   * whose Save button overwrites a published article is not made safe by an
   * alert above it. The way back stays available so the operator is not stuck.
   */
  if (articleUnreadable) {
    return (
      <Box p={6} maxW="1200px" mx="auto">
        <Button
          leftIcon={<MdArrowBack />}
          variant="ghost"
          size="sm"
          mb={4}
          onClick={() => navigate('/admin/help-center')}
        >
          {translate('help_center.back_to_articles')}
        </Button>
        <InlineErrorState message={translate('common.unexpected_error')} />
      </Box>
    );
  }

  return (
    <Box p={6} maxW="1200px" mx="auto">
      {/* Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <Button
          leftIcon={<MdArrowBack />}
          variant="ghost"
          size="sm"
          onClick={() => navigate('/admin/help-center')}
        >
          {translate('help_center.back_to_articles')}
        </Button>
        <ButtonGroup size="sm">
          {!isPublished && (
            <Button
              variant="outline"
              onClick={() => handleSave('draft')}
              isLoading={isSaving}
            >
              {translate('help_center.save_draft')}
            </Button>
          )}
          {isPublished ? (
            <Button
              colorScheme="brand"
              onClick={() => handleSave('published')}
              isLoading={isSaving}
            >
              {translate('help_center.save')}
            </Button>
          ) : (
            <Button
              colorScheme="brand"
              onClick={() => handleSave('published')}
              isLoading={isSaving}
            >
              {translate('help_center.publish')}
            </Button>
          )}
        </ButtonGroup>
      </Flex>

      {/* Metadata Fields */}
      <Box bg="white" borderRadius="lg" border="1px solid" borderColor="gray.200" p={5} mb={5}>
        <Flex gap={4} wrap="wrap">
          <FormControl flex="2" minW="250px">
            <FormLabel fontSize="sm">{translate('help_center.article_title')}</FormLabel>
            <Input
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                if (!slugManuallyEdited) {
                  setSlug(slugify(e.target.value));
                }
                if (!contentKey || !slugManuallyEdited) {
                  setContentKey(slugify(e.target.value));
                }
              }}
              placeholder="Article title"
            />
          </FormControl>

          <FormControl flex="1" minW="200px">
            <FormLabel fontSize="sm">{translate('help_center.article_slug')}</FormLabel>
            <Input
              value={slug}
              onChange={(e) => {
                setSlugManuallyEdited(true);
                setSlug(e.target.value);
              }}
              placeholder="auto-generated-from-title"
            />
          </FormControl>

          <FormControl flex="1" minW="200px">
            <FormLabel fontSize="sm">{translate('help_center.article_content_key')}</FormLabel>
            <Input
              value={contentKey}
              onChange={(e) => setContentKey(e.target.value)}
              placeholder="Links FR/EN versions"
            />
          </FormControl>
        </Flex>

        <Flex gap={4} mt={4} wrap="wrap">
          <FormControl flex="1" minW="180px">
            <FormLabel fontSize="sm">{translate('help_center.article_category')}</FormLabel>
            {/* An empty select here said "this article can belong to no
                category", which is never true of this feature: the API requires
                one. Saying the list could not be read is the difference between
                an operator who waits and one who files an article under
                whatever the form lets them pick. */}
            {categoriesUnavailable ? (
              <InlineErrorState message={translate('common.unexpected_error')} />
            ) : (
              <Select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                {categories?.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.nameFr} / {cat.nameEn}
                  </option>
                ))}
              </Select>
            )}
          </FormControl>

          <FormControl flex="0 0 120px">
            <FormLabel fontSize="sm">{translate('help_center.article_language')}</FormLabel>
            <HStack>
              <Tag
                size="lg"
                variant={language === 'fr' ? 'solid' : 'outline'}
                colorScheme="brand"
                cursor="pointer"
                onClick={() => setLanguage('fr')}
              >
                FR
              </Tag>
              <Tag
                size="lg"
                variant={language === 'en' ? 'solid' : 'outline'}
                colorScheme="brand"
                cursor="pointer"
                onClick={() => setLanguage('en')}
              >
                EN
              </Tag>
            </HStack>
          </FormControl>

          <FormControl flex="0 0 80px">
            <FormLabel fontSize="sm">Order</FormLabel>
            <Input
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)}
              size="sm"
            />
          </FormControl>
        </Flex>

        <FormControl mt={4}>
          <FormLabel fontSize="sm">{translate('help_center.article_excerpt')}</FormLabel>
          <Textarea
            value={excerpt}
            onChange={(e) => setExcerpt(e.target.value)}
            placeholder="Short description for listings..."
            rows={2}
          />
        </FormControl>

        <FormControl mt={4}>
          <FormLabel fontSize="sm">{translate('help_center.article_cover')}</FormLabel>
          <Input
            value={coverImage}
            onChange={(e) => setCoverImage(e.target.value)}
            placeholder="Cover image URL (or use the image upload in the editor)"
          />
        </FormControl>
      </Box>

      {/* TipTap Editor */}
      <Box bg="white" borderRadius="lg" border="1px solid" borderColor="gray.200" overflow="hidden">
        <TipTapEditor
          content={content}
          onChange={setContent}
          onImageUpload={handleImageUpload}
          placeholder="Start writing your article..."
        />
      </Box>
    </Box>
  );
}
